export enum VisitType {
  DELIVERY = 'DELIVERY',
  PICKUP = 'PICKUP',
}

export interface FindPendingShippingResponse {
  number: number;
  externalId?: string;
  serviceType: string;
  visitType: VisitType;
  serviceVisitTypeName: string;
  destinationAddress: {
    streetName: string;
    doorNumber: string;
    apartmentNumber?: string | null;
    corner?: string | null;
    zipCode?: string | null;
    lat: string | number;
    long: string | number;
  } | null;
  originAddress: {
    streetName: string;
    doorNumber: string;
    lat: string | number;
    long: string | number;
  } | null;
}

export interface ShippingForRouting {
  number: number;
  recipient: string;
  recipientPhoneNumber: string;
  commerceShippingNumber: string;
  destinationAddress: {
    streetName: string;
    doorNumber: string;
    apartmentNumber?: string | null;
    lat: number;
    long: number;
  } | null;
  originAddress: {
    streetName: string;
    doorNumber: string;
    lat: number;
    long: number;
  } | null;
  commerce: {
    id: string;
    name: string;
  } | null;
  commercialService: {
    id: string;
    name: string;
    commercialName: string;
    color?: string;
    colors?: {
      pickup?: string;
      delivery?: string;
    };
  } | null;
  status: {
    id: string;
    name: string;
    commercialName: string;
  } | null;
  serviceWithOperator?: {
    id: string;
    commercialService?: {
      id: string;
      name: string;
      commercialName: string;
      color?: string;
    };
  } | null;
  dateToDelivery: string;
  deliveryDetails?: string;
  timeWindowStart?: string;
  timeWindowEnd?: string;
  logisticOperatorId?: string;
  logisticOperatorName?: string | null;
  visitType?: VisitType;
  serviceVisitTypeName?: string;
  serviceType?: string;
  logisticOperator?: {
    name: string;
    businessName?: string | null;
  } | null;
  packageQuantity?: number | null;
}

export interface ShippingsForRoutingResponse {
  shippings: ShippingForRouting[];
  total: number;
}

export interface Zone {
  id: string;
  name: string;
  color: string | null;
  geojson: unknown;
  zoneTypeId: string;
  logisticOperatorId: string;
  isPolygon?: boolean;
  zoneType?: { id: string; name: string };
}

export interface ZonesForRoutingResponse {
  zones: Zone[];
}

export interface RoutePoint {
  lat: number;
  long: number;
  order: number;
  tag?: string;
  shippings?: Array<{
    number: number;
    recipient: string;
    type: VisitType;
    color?: string;
    address: {
      streetName: string;
      doorNumber: string;
    } | null;
    timeWindowStart?: string;
    timeWindowEnd?: string;
  }>;
  travelTime?: number;
  travelDistanceMeters?: number;
  distanceMeters?: number;
  stopSeconds?: number;
  arrivalTime?: string;
  departureTime?: string;
  coordinatesChanged?: boolean;
  changedByUserId?: string;
  precedenceGroupId?: string;
  precedenceOrder?: number;
  id?: string;
  visitId?: string;
  packagesCount?: number;
  monitoringVisitStatus?: 'PENDING' | 'COMPLETED' | 'CANCELLED' | 'FAILED' | 'EXPIRED';
}

export interface EnrichRoutePointDto {
  lat: number;
  long: number;
  tag?: string;
  durationFromPrevious?: number;
  distanceFromPrevious?: number;
  arrivalTime?: string;
  departureTime?: string;
  travelTime?: number;
  [key: string]: unknown;
}

export interface RouteGenerateResponse {
  travelSeconds: number;
  stopSeconds: number;
  peakFactor: number;
  totalSeconds: number;
  totalDistanceMeters?: number;
  route: RoutePoint[];
}

export interface CreateRouteVisit {
  id?: string;
  shippingNumber: number;
  type: VisitType;
  order: number;
  scanOrder: number;
  scheduledFrom: string;
  scheduledTo: string;
  lat: number;
  long: number;
  coordinatesChanged?: boolean;
  changedByUserId?: string;
}

export interface CreateRouteRequest {
  id?: string;
  name: string;
  scheduled: string;
  totalSeconds: number;
  visits: CreateRouteVisit[];
  source?: 'WEB' | string;
  driverId?: string;
}

export interface CreateRouteVisitResponse {
  id: string;
  shippingNumber: number;
  type: VisitType;
  order: number;
  scheduledFrom: string;
  scheduledTo: string;
  lat: number;
  long: number;
  coordinatesChanged: boolean;
  changedByUserId?: string;
}

export interface CreateRouteResponse {
  route: {
    id: string;
    name: string;
    scheduled: string;
    status: string;
    totalSeconds: number;
  };
  visits: CreateRouteVisitResponse[];
}

export interface ShippingInputDto {
  number: string;
  scanOrder: number;
  logisticOperatorId: string;
  lat?: number;
  long?: number;
}

export interface GenerateRouteRequest {
  shippings: ShippingInputDto[];
  routeLogisticOperatorId?: string;
  strategy?: string;
  baseLat?: number;
  baseLong?: number;
  polygon?: {
    type: string;
    coordinates: number[][][];
  };
  numbers?: string[];
}

export interface RouteOperatorCandidate {
  logisticOperatorId: string;
  logisticOperatorName: string | null;
}

export interface ResolveRouteOperatorResponse {
  logisticOperatorId: string;
  logisticOperatorName: string | null;
  mixed: boolean;
  shippingOperatorIds: string[];
  candidates: RouteOperatorCandidate[];
}

export interface RemoveShippingsSkippedItem {
  number: number;
  reason: string;
}

export interface RemoveShippingsFromRouteResponse {
  removed: number[];
  skipped: RemoveShippingsSkippedItem[];
}
